import React from "react";
import styled from "@emotion/styled";

type AsProp<T extends React.ElementType> = {
  as?: T;
};

type PropsToOmit<T extends React.ElementType, P> = keyof (AsProp<T> & P);

type PolymorphicComponentProp<T extends React.ElementType, Props = {}> = React.PropsWithChildren<Props & AsProp<T>> &
  Omit<React.ComponentPropsWithoutRef<T>, PropsToOmit<T, Props>>;

interface TextProps {
  children: React.ReactNode;
  color?: string;
  fontSize?: string | number;
  fontWeight?: string | number;
  lineHeight?: string | number;
  textEllipsis?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

type TextComponent = <T extends React.ElementType = "div">(
  props: PolymorphicComponentProp<T, TextProps>
) => React.ReactElement | null;

const StyledText = styled.div<Omit<TextProps, "children">>`
  color: ${({ color }) => color || "inherit"};
  font-size: ${({ fontSize }) => (typeof fontSize === "number" ? `${fontSize}px` : fontSize || "inherit")};
  font-weight: ${({ fontWeight }) => fontWeight || "inherit"};
  line-height: ${({ lineHeight }) => (typeof lineHeight === "number" ? `${lineHeight}%` : lineHeight || "150%")};
  overflow: ${({ textEllipsis }) => (textEllipsis ? "hidden" : "visible")};
  text-overflow: ${({ textEllipsis }) => (textEllipsis ? "ellipsis" : "clip")};
  white-space: ${({ textEllipsis }) => (textEllipsis ? "nowrap" : "normal")};
  width: ${({ textEllipsis }) => (textEllipsis ? "100%" : "auto")};
`;

export const Text: TextComponent = ({
  as,
  children,
  color,
  fontSize,
  fontWeight,
  lineHeight = 150,
  textEllipsis = false,
  className,
  style,
  ...props
}) => {
  const Component = as || "div";

  return (
    <StyledText
      as={Component}
      color={color}
      fontSize={fontSize}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      textEllipsis={textEllipsis}
      className={className}
      style={style}
      {...props}
    >
      {children}
    </StyledText>
  );
};

export default Text;
